<template>
    <div>
        <div class="tk-card-header">
            <h2>Forms</h2>
            <q-btn flat dense no-caps color="primary" icon="o_post_add" label="Complete form" @click="openNewForm" />
        </div>

        <PreviousFormsTable
            ref="formsTable"
            class="engagement-forms-table"
            :subject="formSubject"
            :history-loader="loadForms"
            :display-columns="['form', 'completed', 'completedBy', 'status']"
            :rows-per-page="5"
            :allow-assign="false"
            empty-message="No forms have been completed for this engagement."
            @form-selected="openResponse"
            @form-deleted="$emit('goals-changed')"
            :highlight="false"
        />

        <q-dialog v-model="showNewFormDialog" persistent class="engagement-form-dialog-shell" @hide="onNewDialogHide">
            <q-card class="engagement-form-dialog">
                <q-card-section class="engagement-form-dialog__header">
                    <h2>Complete Form</h2>
                    <q-btn flat round dense icon="close" v-close-popup>
                        <q-tooltip class="text-body2">Close</q-tooltip>
                    </q-btn>
                </q-card-section>
                <q-card-section class="engagement-form-dialog__body engagement-form-dialog__body--start">
                    <DynamicFormComponent
                        v-if="formSubject"
                        :initial-subject="formSubject"
                        @exit="onFormClosed"
                        @form-submitted="onFormSubmitted"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>

        <q-dialog v-model="showResponseDialog" persistent class="engagement-form-dialog-shell" @hide="onResponseDialogHide">
            <q-card class="engagement-form-dialog">
                <q-card-section class="engagement-form-dialog__header">
                    <h2>Form Response</h2>
                    <q-btn flat round dense icon="close" v-close-popup>
                        <q-tooltip class="text-body2">Close</q-tooltip>
                    </q-btn>
                </q-card-section>
                <q-card-section class="engagement-form-dialog__body">
                    <DynamicFormRenderer
                        v-if="selectedResponse && formSubject"
                        :form-id="selectedResponse.form_id"
                        :subject="formSubject"
                        :_responseId="selectedResponse.id"
                        @exit="onResponseClosed"
                        @form-submitted="onFormSubmitted"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import DynamicFormComponent from "@/components/DynamicFormComponents/Main.vue";
import DynamicFormRenderer from "@/components/DynamicFormComponents/DynamicFormRenderer.vue";
import PreviousFormsTable from "@/components/DynamicFormComponents/PreviousFormsTable.vue";
import api from "@/services/api/api";
import { subjectWithClientEngagement, tkPersonSubject } from "@/services/util/formSubject";

export default {
    name: "TkEngagementFormsPanel",
    components: { DynamicFormComponent, DynamicFormRenderer, PreviousFormsTable },
    emits: ["goals-changed"],
    props: {
        engagement: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showNewFormDialog: false,
            showResponseDialog: false,
            selectedResponse: null,
            newDialogChanged: false,
            responseDialogChanged: false,
        };
    },
    computed: {
        formSubject() {
            const subject = tkPersonSubject({
                id: this.engagement.tk_person_id,
                first_name: this.engagement.first_name,
                last_name: this.engagement.last_name,
                preferred_name: this.engagement.preferred_name,
                email: this.engagement.email,
                phone: this.engagement.phone,
            });
            return subjectWithClientEngagement(subject, this.engagement.id);
        },
    },
    methods: {
        async loadForms() {
            if (!this.engagement?.id) return [];
            const res = await api.forms.getEngagementFormHistory(this.engagement.id);
            return res.data?.data || [];
        },
        openNewForm() {
            this.newDialogChanged = false;
            this.showNewFormDialog = true;
        },
        openResponse(responseId, formId) {
            this.responseDialogChanged = false;
            this.selectedResponse = { id: responseId, form_id: formId };
            this.showResponseDialog = true;
        },
        onFormClosed() {
            this.showNewFormDialog = false;
        },
        onResponseClosed() {
            this.showResponseDialog = false;
        },
        onNewDialogHide() {
            const changed = this.newDialogChanged;
            this.newDialogChanged = false;
            if (!changed) return;
            this.$refs.formsTable?.fetchPreviousForms();
            this.$emit("goals-changed");
        },
        onResponseDialogHide() {
            this.selectedResponse = null;
            const changed = this.responseDialogChanged;
            this.responseDialogChanged = false;
            if (!changed) return;
            this.$refs.formsTable?.fetchPreviousForms();
            this.$emit("goals-changed");
        },
        onFormSubmitted() {
            if (this.showNewFormDialog) this.newDialogChanged = true;
            if (this.showResponseDialog) this.responseDialogChanged = true;
            this.showNewFormDialog = false;
            this.showResponseDialog = false;
            this.selectedResponse = null;
        },
    },
};
</script>

<style scoped>
.engagement-form-dialog {
    display: flex;
    flex-direction: column;
    width: min(1000px, 98vw);
    max-height: calc(100vh - 40px);
    overflow: hidden;
}

.engagement-form-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 18px 16px 10px;
}

.engagement-form-dialog__header h2 {
    margin: 0;
    color: #3d4b46;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.035em;
    line-height: 1.25;
}

.engagement-form-dialog__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 12px 32px 32px;
}

.engagement-form-dialog__body--start :deep(.form-container) {
    width: 100%;
}

.engagement-form-dialog__body--start :deep(.form-container > span),
.engagement-form-dialog__body--start :deep(.q-gutter-md) {
    display: block;
}

.engagement-form-dialog__body--start :deep(.q-gutter-md),
.engagement-form-dialog__body--start :deep(.q-gutter-md > *) {
    margin: 0;
}

.engagement-form-dialog__body--start :deep(h4) {
    display: none;
}

.engagement-form-dialog__body--start :deep(.form-card) {
    width: 100%;
    max-width: none;
    padding: 0;
    border-radius: 0;
}

.engagement-form-dialog__body--start :deep(.q-field),
.engagement-form-dialog__body--start :deep(.q-table__container),
.engagement-form-dialog__body--start :deep(.q-banner) {
    width: 100%;
}

.engagement-form-dialog__body--start :deep(.form-card p) {
    margin-top: 0;
}

.engagement-form-dialog__body--start :deep(.q-banner) {
    min-height: 46px;
}

.engagement-form-dialog__body--start :deep(.form-actions) {
    justify-content: flex-end;
    margin-top: 18px;
}

.engagement-form-dialog__body--start :deep(.form-actions .q-btn) {
    min-width: 150px;
}

.engagement-form-dialog__body :deep(.dynamic-form-page) {
    width: 100%;
    max-width: none;
}

.engagement-form-dialog__body :deep(.dynamic-form-page-content) {
    width: 100%;
}

.engagement-forms-table :deep(.form-table) {
    border-color: rgba(47, 62, 70, 0.1);
}

.engagement-forms-table :deep(.q-table th) {
    color: #748078;
    font-weight: 500;
}
</style>

<style>
@media (min-width: 600px) {
    .engagement-form-dialog-shell.q-dialog__inner--minimized > div,
    .engagement-form-dialog-shell .q-dialog__inner--minimized > div {
        max-width: 98vw;
    }
}
</style>
